import { supabase } from '@/lib/supabase';
import { logger } from '@/utils/logger';
import { fetchProfilesByIds } from '@/utils/fetchProfilesByIds';
import {
  fetchEventShiftRoster,
  fetchEventShifts,
} from '@/utils/eventShiftsApi';
import { saveBytes } from '@/utils/saveFile';
import {
  buildEventExportRows,
  buildExportFileName,
  type EventExportRow,
  type ExportProfile,
  type ExportVolunteerRow,
} from '@/utils/eventExport';
import type { CellObject, SheetData } from 'write-excel-file/browser';

const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const HEADERS = ['Name', 'Age', 'Department', 'Shifts', 'T-shirt size'];
const COLUMN_WIDTHS = [28, 8, 28, 32, 14];
/** Points per wrapped line, so a volunteer with several shifts gets a tall enough row. */
const LINE_HEIGHT = 15;

export type ExportEventVolunteersResult =
  | { ok: true; count: number }
  | { ok: false; empty: true };

/**
 * Exports an event's accepted volunteers (name, age, departments, shifts, t-shirt size) as an
 * .xlsx file: a download on web, the share sheet on native. Reads fresh data instead of the
 * cached roster, whose profile select has no age or t-shirt size, and loads the shifts itself
 * because the Volunteers page only loads them while the Shifts tab is open.
 *
 * Resolves `{ ok: false, empty: true }` when there is nobody to export; throws when a read or
 * the save failed, so the caller can toast.
 */
export async function exportEventVolunteers(
  eventId: string,
  eventName: string | null | undefined
): Promise<ExportEventVolunteersResult> {
  const { data: volunteerRows, error } = await supabase
    .from('volunteers')
    .select('volunteer_id, department')
    .eq('event_id', eventId)
    .eq('is_active', true);
  if (error) {
    logger.error('exportEventVolunteers: volunteers read failed', {
      eventId,
      error,
    });
    throw new Error('Could not load the volunteers.');
  }

  const volunteers = (volunteerRows ?? []) as ExportVolunteerRow[];
  if (volunteers.length === 0) return { ok: false, empty: true };

  const [profilesResult, shifts, roster] = await Promise.all([
    fetchProfilesByIds<ExportProfile>(
      volunteers.map((row) => row.volunteer_id),
      'id, first_name, last_name, date_of_birth, age, tshirtSize',
      'exportEventVolunteers'
    ),
    fetchEventShifts(eventId),
    fetchEventShiftRoster(eventId),
  ]);
  if (profilesResult.failed || shifts == null || roster == null) {
    throw new Error('Could not load the volunteer details.');
  }

  const rows = buildEventExportRows({
    volunteers,
    profilesById: profilesResult.profilesById,
    shifts,
    roster,
  });

  const blob = await writeWorkbook(rows);
  const bytes = new Uint8Array(await blob.arrayBuffer());
  const title = eventName?.trim()
    ? `${eventName.trim()} volunteers`
    : 'Volunteers';
  await saveBytes(bytes, buildExportFileName(eventName), XLSX_MIME, title);
  return { ok: true, count: rows.length };
}

async function writeWorkbook(rows: EventExportRow[]): Promise<Blob> {
  // Loaded on demand so the spreadsheet writer stays out of the main bundle.
  const { default: writeXlsxFile } = await import('write-excel-file/browser');
  const header: CellObject[] = HEADERS.map((value) => ({
    value,
    fontWeight: 'bold',
  }));
  const body: SheetData = rows.map((row) => {
    const lines = Math.max(1, row.shifts.split('\n').length);
    // Top-aligned so the other columns line up with the first shift; the shifts cell wraps
    // (one shift per line) instead of spilling into the t-shirt column.
    const cell = (value: string | number | null): CellObject => ({
      value: value ?? undefined,
      alignVertical: 'top',
    });
    return [
      cell(row.name),
      cell(row.age),
      cell(row.department),
      { ...cell(row.shifts), wrap: true, height: lines * LINE_HEIGHT },
      cell(row.tshirtSize),
    ];
  });
  return writeXlsxFile([header, ...body], {
    sheet: 'Volunteers',
    columns: COLUMN_WIDTHS.map((width) => ({ width })),
    stickyRowsCount: 1,
  }).toBlob();
}
